import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { track } from '../services/analyticsTracker';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, Plus, Search, Filter, ArrowLeft, Send, Paperclip, 
  CheckCircle, Clock, XCircle, User, Calendar, AlertCircle, Image as ImageIcon,
  Loader2, MessageCircle, ChevronDown, ArrowUp, ArrowDown, Lock, Trash2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
// FIX #118/#119 (F-10 reopen, 2026-09-24): native alert() on submit success blocked
// the main thread and read like a UI freeze. Non-blocking toast instead.
import Toast, { useToast } from '../components/Toast';
import { useDataFilter, useCanModify } from '../hooks/useDataFilter';
import { useEntitlements } from '../hooks/useEntitlements';
import { complaintsAPI, api, is429Backoff, reset429Backoff } from '../services/api';
import { visitors } from '../services/api-compat';

type BackendComplaintStatus = 'open' | 'in_progress' | 'resolved';

const STATUS_LABELS: Record<BackendComplaintStatus, string> = {
  open: 'Abierta',
  in_progress: 'En proceso',
  resolved: 'Cerrada',
};

interface ComplaintStats {
  total: number;
  abiertas: number;
  enProceso: number;
  cerradas: number;
}

// FIX (2026-09-02, Jaime): the DB stores Spanish statuses ('abierta', 'en_proceso')
// plus closed variants, while the UI historically compared against 'open'/'in_progress'.
// Normalize every row once at fetch time so badges, filters, and forms match.
// FIX #343 (2026-10-06): a stale/non-UUID condoId (e.g. a leftover localStorage
// 'user' snapshot after a condo switch) reached the complaints API and was
// rejected with 400 "Parámetro condoId inválido". Validate the shape client-side
// with the same pattern the billing routes use server-side
// (api/src/modules/billing/routes.js: UUID_RE + early-return guard).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v: unknown): boolean => typeof v === 'string' && UUID_RE.test(v);

const CLOSED_STATUS_VARIANTS = ['resolved', 'resuelta', 'resuelto', 'cerrada', 'cerrado', 'closed'];
function normalizeComplaintStatus(raw: any): BackendComplaintStatus {
  const v = String(raw ?? '').toLowerCase();
  if (CLOSED_STATUS_VARIANTS.includes(v)) return 'resolved';
  if (v === 'en_proceso' || v === 'in_progress' || v === 'en proceso') return 'in_progress';
  return 'open';
}

interface Complaint {
  id: string;
  title: string;
  description: string;
  category: 'queja' | 'sugerencia' | 'peticion' | 'bug';
  status: BackendComplaintStatus;
  createdAt: string;
  updatedAt: string;
  createdBy: string;  // userId of creator
  createdByName: string;  // display name
  createdByUnit: string;  // unit number
  createdByAvatar?: string;
  creatorRole?: string;  // FEATURE (2026-09-02, Jaime): role of the creator (propietario/residente/personal/admin). Absent when backend hasn't shipped it -> badge hidden.
  condoId?: string;
  userId?: string;
  unit?: string;
  assignedTo?: string;
  priority: 'baja' | 'media' | 'alta';
  scope?: 'condo' | 'app';
  isPrivate?: boolean;
  images: string[];
  attachmentUrl?: string;
  attachmentUrls?: string[];
  messages: Message[];
  // Suggestion voting (2026-09-23): snake_case API -> camelCase in loaders below.
  voteScore?: number;  // net score (up - down)
  voteUp?: number;
  voteDown?: number;
  myVote?: number;     // 0 none | 1 up | -1 down
  isOwn?: boolean;     // caller created this complaint (highlight own suggestions)
  commentCount?: number; // community comments on a public suggestion (Jaime 2026-09-23 ruling)
}

// Suggestion comments (2026-09-23, Jaime ruling 19:13): community discussion
// thread on PUBLIC app-scope 'sugerencia' rows. Unlike the private support
// thread (complaint_messages), comments are attributed to commenting users and
// visible to everyone. Bugs (private intake) never render comments.
interface SuggestionComment {
  id: string;
  userId: string;
  authorName: string;
  body: string;
  createdAt: string;
}



interface Message {
  id: string;
  text: string;
  sender: 'resident' | 'admin' | 'assistant';
  senderName: string;
  timestamp: string;
  attachments?: string[];
}

// Mock data removed — page fetches real data from complaintsAPI

// MULTI-IMAGE (2026-09-02, Jaime): merge legacy attachment_url with the new
// attachment_urls array (migration 100) so old rows keep rendering unchanged.
function mapAttachmentUrls(r: any): string[] {
  const raw = r?.attachmentUrls ?? r?.attachment_urls;
  let urls: string[] = Array.isArray(raw) ? raw.filter((u: any) => typeof u === 'string' && u) : [];
  const first = r?.attachmentUrl ?? r?.attachment_url;
  if (first && !urls.includes(first)) urls = [first, ...urls];
  return urls;
}

// PACKET (2026-09-24, Jaime): lightweight inline-SVG dual-series chart for the
// super-admin bugs trend (found/day bars + resolved/day line). No new deps.
function BugTrendChart({ data }: { data: any }) {
  const found: { date: string; count: number }[] = data?.found ?? [];
  const resolved: { date: string; count: number }[] = data?.resolved ?? [];
  if (!found.length) return null;
  const W = 720, H = 190, padL = 30, padR = 8, padT = 10, padB = 24;
  const n = found.length;
  const step = (W - padL - padR) / n;
  const max = Math.max(1, ...found.map(d => d.count), ...resolved.map(d => d.count));
  const x = (i: number) => padL + (i + 0.5) * step;
  const y = (v: number) => padT + (H - padT - padB) * (1 - v / max);
  const bw = Math.min(14, Math.max(2, step * 0.55));
  const linePts = resolved.map((d, i) => `${x(i)},${y(d.count)}`).join(' ');
  const fmt = (d: string) => d?.slice(5);
  return (
    <div>
      <div className="flex items-center gap-4 mb-1 text-xs font-bold text-[var(--v-text-secondary)]">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500 inline-block" /> Encontradas</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-1 rounded bg-emerald-500 inline-block" /> Resueltas</span>
        <span className="ml-auto">Máx/día: {max}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Tendencia de bugs por día">
        <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="var(--v-border-light)" />
        <text x={2} y={padT + 8} fontSize="10" fill="var(--v-text-tertiary)">{max}</text>
        {found.map((d, i) => (
          <rect key={d.date} x={x(i) - bw / 2} y={y(d.count)} width={bw} height={Math.max(0, H - padB - y(d.count))} rx={1} fill="#3b82f6" opacity={0.85}>
            <title>{`${d.date}: ${d.count} encontradas`}</title>
          </rect>
        ))}
        {n > 1 && (
          <polyline points={resolved.map((d, i) => `${x(i)},${y(d.count)}`).join(' ')} fill="none" stroke="#10b981" strokeWidth={2}>
            <title>Resueltas por día</title>
          </polyline>
        )}
        {resolved.map((d, i) => (
          <circle key={d.date} cx={x(i)} cy={y(d.count)} r={2.5} fill="#10b981">
            <title>{`${d.date}: ${d.count} resueltas`}</title>
          </circle>
        ))}
        {[0, Math.floor((n - 1) / 2), n - 1].filter((v, i, a) => a.indexOf(v) === i).map(i => (
          <text key={i} x={x(i)} y={H - 8} fontSize="10" textAnchor="middle" fill="var(--v-text-tertiary)">{fmt(found[i]?.date)}</text>
        ))}
      </svg>
    </div>
  );
}

export default function QuejasYSugerencias() {
  // FIX #118/#119 (F-10 reopen, 2026-09-24): friendly success toast (see import note).
  const { toasts, removeToast, success } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  // Phase-1c 429 fix (2026-09-30): the 12s silent-refresh interval closes over
  // mount-time state, so prevRows passed to withThreadMessages was always the
  // mount snapshot and cache hits never fired. Keep the latest rows in refs so
  // the interval closure always reads current data.
  const complaintsRef = useRef<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | BackendComplaintStatus>('all');
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'queja' | 'sugerencia' | 'peticion' | 'bug'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
  const [newMessage, setNewMessage] = useState('');

  // F57: Admin response form state
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [responseData, setResponseData] = useState({
    status: 'in_progress' as BackendComplaintStatus,
    response: '',
  });
  const [responseLoading, setResponseLoading] = useState(false);
  const [responseError, setResponseError] = useState('');
  const [responseComplaintId, setResponseComplaintId] = useState<string | null>(null);

  // New complaint form state (previously the form was uncontrolled and never submitted).
  const [newTitle, setNewTitle] = useState('');
  const [newCategory, setNewCategory] = useState<'queja' | 'sugerencia' | 'peticion' | 'bug'>('queja');
  const [newDescription, setNewDescription] = useState('');
  // A1: Destino selector — 'condo' (default) or 'app' (Soporte Vecindus)
  const [newScope, setNewScope] = useState<'condo' | 'app'>('condo');
  // FEATURE (2026-09-02, Jaime): private complaints — only the creator and the
  // administration can see them (backend is_private flag). Only meaningful for
  // Condominio scope; hidden for Soporte Vecindus.
  const [newPrivate, setNewPrivate] = useState(false);
  // FEATURE (2026-09-01, Jaime): image attachment for new complaints.
  const [newImage, setNewImage] = useState<File | null>(null);
  const [newImagePreview, setNewImagePreview] = useState<string>('');
  // A1: admin tabs — Condominio / Soporte Vecindus (super_admin only)
  const [activeTab, setActiveTab] = useState<'condo' | 'app'>('condo');
  // FEATURE (2026-09-23, Jaime 19:49): super-admin split views inside Soporte
  // Vecindus — Todo / Bugs (private intake triage) / Sugerencias (ranked by votes).
  const [appSubTab, setAppSubTab] = useState<'todo' | 'bugs' | 'sugerencias'>('todo');
  const [appComplaints, setAppComplaints] = useState<Complaint[]>([]);
  const appComplaintsRef = useRef<Complaint[]>([]);
  useEffect(() => { complaintsRef.current = complaints; }, [complaints]);
  useEffect(() => { appComplaintsRef.current = appComplaints; }, [appComplaints]);
  // 2026-09-02 (Jaime): Soporte Vecindus is now visible to every user and shows
  // ALL of the caller's own app-scope threads, unit/condo-independent.
  const [appLoaded, setAppLoaded] = useState(false);
  const [appStats, setAppStats] = useState<ComplaintStats>({ total: 0, abiertas: 0, enProceso: 0, cerradas: 0 });
  // Server-computed stats (single source of truth) — see complaints routes.js stats endpoint.
  const [stats, setStats] = useState<ComplaintStats>({ total: 0, abiertas: 0, enProceso: 0, cerradas: 0 });

  // LIVE CARD + ALLOWANCE (2026-09-02, Jaime): the "Vivas" card shows
  // Abiertas + En Proceso (cases still alive). The "/X" quota suffix mirrors
  // the backend complaints gate (src/modules/complaints/routes.js POST /):
  // X = condo_free plan definition limits.complaints (Pricing matrix), counted
  // over non-closed complaints only; paid condo plans are unlimited (no "/X").
  // /api/entitlements/me exposes limits.complaints when present; free-plan
  // entitlements are HOME-dimensional and often omit the condo-dimension key,
  // so we fall back to the backend's own default (PLAN_LIMITS.complaints = 5).
  const { entitlement } = useEntitlements();
  const entPlan = String((entitlement as any)?.plan ?? entitlement?.tier ?? 'free');
  const isPaidCondoContext = entPlan !== 'free' && entPlan !== 'condo_free';
  const complaintsLimit = isPaidCondoContext
    ? null
    : (Number((entitlement as any)?.limits?.condo?.complaints ?? (entitlement as any)?.limits?.complaints) || 5);
  const liveCases = stats.abiertas + stats.enProceso;
  const [newSubmitting, setNewSubmitting] = useState(false);
  const [newError, setNewError] = useState('');
  // FEATURE (2026-09-02, Jaime): chat paperclip — attach an image to the open complaint.
  const [threadAttaching, setThreadAttaching] = useState(false);
  const [threadAttachError, setThreadAttachError] = useState('');
  // PACKET (2026-09-24, Jaime): super-admin bugs trend chart state.
  const [bugTrends, setBugTrends] = useState<any>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendWindow, setTrendWindow] = useState<'7' | '30' | 'custom'>('30');
  const [trendFrom, setTrendFrom] = useState('');
  const [trendTo, setTrendTo] = useState('');
  // PACKET (2026-09-24, Jaime): collapsed resolved sections.
  const [showResolvedBugs, setShowResolvedBugs] = useState(false);
  const [showResolvedSugs, setShowResolvedSugs] = useState(false);

  const handleCreateComplaint = async () => {
    if (!newTitle.trim() || !newDescription.trim()) {
      setNewError('Título y descripción son requeridos.');
      return;
    }
    setNewSubmitting(true);
    setNewError('');
    try {
      // FIX #112 (2026-09-24, bug board): the form used to read a STALE
      // localStorage 'user' snapshot. That snapshot is only written at login and
      // at full context activation (activateContext); the AppShell/MobileMenu
      // context-switch paths (#91/#92: setActiveCondo / activatePersonalContext)
      // mutate the LIVE AuthContext user WITHOUT re-persisting localStorage, so
      // after any Personal <-> condo switch the form resolved an outdated
      // condoId (empty after a Personal persist -> 400 "Faltan campos
      // requeridos", or a stale/non-UUID value -> "Parámetro condoId inválido").
      // Read the live context user instead. For Condominio scope with no active
      // condo, fail fast with a friendly message instead of silently POSTing an
      // empty condoId.
      const ctxUser = user || (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
      const createdScope = newScope;
      // FIX #343 (2026-10-06): the #112 guard only rejected an EMPTY condoId;
      // a stale/non-UUID value still reached POST /complaints and 400'd with
      // "Parámetro condoId inválido". Require a present, well-formed UUID
      // for condo scope before firing the call.
      if (createdScope === 'condo' && !isUuid(ctxUser.condoId)) {
        setNewError('No hay un condominio activo. Selecciona un condominio en el selector de contexto para enviar una queja a administración, o usa el destino "Soporte Vecindus (app)".');
        setNewSubmitting(false);
        return;
      }
      const created = await complaintsAPI.create({
        title: newTitle.trim(),
        description: newDescription.trim(),
        category: newCategory,
        // FIX #343: app-scope complaints do not need a condo; drop a stale
        // non-UUID condoId instead of sending it and getting a 400.
        condoId: createdScope === 'app' ? (isUuid(ctxUser.condoId) ? ctxUser.condoId : null) : ctxUser.condoId,
        unit: ctxUser.unit || ctxUser.unitLabel,
        scope: newScope,
        // BUG #112 follow-up (2026-09-24): POST /api/complaints reads req.body.isPrivate
        // (camelCase). The form previously sent snake_case `is_private`, so the
        // "Privada" checkbox was silently dropped server-side (always false).
        isPrivate: newPrivate && newScope === 'condo',
      });
      track('complaints_complaint_created');
      // Upload optional image (re-encoded to JPEG ≤1024px like the avatar flow).
      const newId = (created as any)?.data?.data?.id ?? (created as any)?.data?.id;
      if (newImage && newId) {
        try {
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const url = URL.createObjectURL(newImage);
            const img = new Image();
            img.onload = () => {
              try {
                const maxDim = 1024;
                const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
                const canvas = document.createElement('canvas');
                canvas.width = Math.max(1, Math.round(img.width * scale));
                canvas.height = Math.max(1, Math.round(img.height * scale));
                canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL('image/jpeg', 0.85));
              } finally { URL.revokeObjectURL(url); }
            };
            img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('formato')); };
            img.src = url;
          });
          await api.post(`/complaints/${newId}/attachment`, { file: dataUrl });
        } catch (attErr) {
          console.error('attachment failed', attErr); // non-fatal: case already created
        }
      }
      setShowNewModal(false);
      setNewTitle('');
      setNewDescription('');
      setNewCategory('queja');
      setNewScope('condo');
      setNewPrivate(false);
      setNewImage(null); setNewImagePreview('');
      // FIX #76 (2026-09-23, bug board): the submit handler used to AWAIT a full
      // list reload (loadComplaints -> withThreadMessages fires one GET per row).
      // With 60+ rows that serialized behind the browser connection pool, the
      // button stayed in "Enviando..." for a long time (UI freeze) and users
      // re-submitted the same complaint (duplicate rows). Reset the form/close
      // the modal IMMEDIATELY and refresh the list in the background instead.
      loadComplaints({ silent: true }).catch(() => {});
      if (createdScope === 'app') { setAppComplaints([]); setAppLoaded(false); }
      // Scope-visibility fix (2026-09-30): the two scope views (Condominio /
      // Soporte Vecindus) are intentionally disjoint read paths (backend
      // VISIBILITY RULING: condo list strictly scope='condo', app lists keyed
      // on user_id). After creating, land the user on the tab that owns the
      // new record so it is immediately visible instead of "missing" from the
      // tab they were on. The app tab reloads above (appComplaints reset).
      if (activeTab !== createdScope) setActiveTab(createdScope);
      success('✅ Enviada correctamente');
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'No se pudo enviar la queja. Intenta de nuevo.';
      setNewError(msg);
    } finally {
      setNewSubmitting(false);
    }
  };

  const { isAdmin, isSuperAdmin } = useAuth();

  useEffect(() => {
    loadComplaints();
  }, []);

  // A1/2026-09-02: load the Soporte Vecindus tab for EVERY user when it opens.
  // super_admin sees all app-scope threads (GET /complaints/app); everyone else
  // sees their own, unit-independent (GET /complaints/mine/app). Defensive
  // filter below keeps app-scope rows out of the condo tab.
  useEffect(() => {
    if (activeTab === 'app' && !appLoaded) {
      loadAppComplaints();
    }
  }, [activeTab, appLoaded]);

  // FIX #2 (2026-09-02, Jaime): preload every visible case's thread with the list.
  // One parallel GET /complaints/:id/messages per row (Promise.allSettled so a
  // single failure doesn't break the list); results are cached in state so the
  // "N mensajes" badge renders immediately and stats fallback sees replies.
  // NOTE: if the backend later adds messages_count to the list payload, this can
  // be replaced by reading the count directly from the row.
  // Phase-1 429-incident fix (2026-09-30): the previous signature refetched one
  // GET /complaints/:id/messages per row on EVERY silent refresh (12s poll), a
  // ~100-row fan-out ≈ 500 req/min that exhausted the 2000/15min global bucket
  // (measured: 215x429, one session, bursts to 83 req/min, complaint ids
  // 88-99/207-211 each fetched 11x in ~2min). Now cached threads are reused via
  // the prevRows snapshot (keyed by id + updatedAt), so only new/changed rows
  // fire a GET. The 4s open-thread poll keeps the selected thread fresh.
  const withThreadMessages = async (rows: any[], prevRows: any[]) => {
    const prevById = new Map(prevRows.map((p: any) => [String(p.id), p]));
    // Kimi review 2026-09-23: app-scope rows are private intake — a non-owner who
    // is not super_admin always gets 403/404 on the thread endpoint, so skip the
    // preload entirely for those rows instead of firing guaranteed-failing GETs
    // (N wasted 403s per app-tab load). Thread loads on open instead.
    // P1-2 fix (2026-09-30, Phase-1b peer review): cache hits must NOT flow back
    // into the mapping stage — prev.messages are already UI-shaped Message
    // objects, and re-running threadToMessage over them produced undefined
    // body/author/created_at on every second poll (sender forced to 'admin',
    // stats fallback counting everything 'En Proceso'). Cache hits are
    // short-circuited below and returned with messages attached directly.
    const cacheHit = (r: any): boolean => {
      const prev = prevById.get(String(r.id));
      return !!(
        prev &&
        prev.__msgsLoaded &&
        String(prev.updatedAt ?? prev.updated_at ?? '') === String(r.updatedAt ?? r.updated_at ?? '')
      );
    };
    const results = await Promise.allSettled(rows.map(r => {
      if (cacheHit(r)) return Promise.resolve(null);
      return (r.scope === 'app' && !r.isOwn && !isSuperAdmin())
        ? Promise.resolve(null)
        : api.get(`/complaints/${r.id}/messages`);
    }));
    return rows.map((r, i) => {
      const res = results[i];
      const prev = prevById.get(String(r.id));
      // P1-2: cache hit — attach the already-shaped messages, skip mapping.
      if (cacheHit(r)) return { ...r, messages: prev.messages, __msgsLoaded: true };
      if (res.status === 'fulfilled' && res.value) {
        const list = res.value?.data?.data;
        if (Array.isArray(list)) return { ...r, messages: list.map(threadToMessage), __msgsLoaded: true };
      }
      // Skipped (private app row) or failed fetch: keep whatever we had cached.
      return { ...r, messages: prev?.messages ?? r.messages, __msgsLoaded: prev?.__msgsLoaded ?? false };
    });
  };

  // Same Abiertas/En Proceso/Cerradas semantics as the server stats endpoints;
  // used as the fallback when /mine/app/stats is unavailable.
  const deriveAppStats = (rows: any[]): ComplaintStats => {
    const closed = (c: any) => c.status === 'resolved';
    const hasReply = (c: any) => (c.messages || []).some((m: any) => m.sender === 'admin' || m.sender === 'assistant');
    return {
      total: rows.length,
      abiertas: rows.filter((c: any) => !closed(c) && !hasReply(c)).length,
      enProceso: rows.filter((c: any) => !closed(c) && hasReply(c)).length,
      cerradas: rows.filter((c: any) => closed(c)).length,
    };
  };

  // SILENT REFRESH (2026-09-02, Jaime): silent=true keeps the current list on a
  // failed background poll instead of wiping it with an empty state.
  const loadAppComplaints = async (opts?: { silent?: boolean }) => {
    try {
      const response = isSuperAdmin()
        ? await complaintsAPI.getApp()
        : await complaintsAPI.getMineApp();
      const envelope = response && response.data !== undefined ? response.data : response;
      const payload = envelope && envelope.data !== undefined ? envelope.data : envelope;
      const rows = Array.isArray(payload) ? payload : [];
      const mapped = rows.map((r: any) => ({
        ...r,
        id: String(r.id),
        condoId: r.condoId ?? r.condo_id,
        userId: r.userId ?? r.user_id,
        createdBy: r.createdBy ?? r.created_by ?? r.user_id ?? '',
        createdByName: r.createdByName ?? r.created_by_name ?? 'Usuario',
        createdByUnit: r.createdByUnit ?? r.unit ?? '',
        creatorRole: r.creatorRole ?? r.creator_role ?? '',
        status: normalizeComplaintStatus(r.status),
        createdAt: r.createdAt ?? r.created_at ?? '',
        updatedAt: r.updatedAt ?? r.updated_at ?? '',
        scope: (r.scope ?? 'app') as 'condo' | 'app',
        isPrivate: !!(r.isPrivate ?? r.is_private),
        images: Array.isArray(r.images) ? r.images : [],
        attachmentUrl: r.attachmentUrl ?? r.attachment_url,
        attachmentUrls: mapAttachmentUrls(r),
        messages: Array.isArray(r.messages) ? r.messages : [],
        voteScore: Number(r.voteScore ?? r.vote_score ?? 0),
        voteUp: Number(r.voteUp ?? r.vote_up ?? 0),
        voteDown: Number(r.voteDown ?? r.vote_down ?? 0),
        myVote: Number(r.myVote ?? r.my_vote ?? 0),
        commentCount: Number(r.commentCount ?? r.comment_count ?? 0),
        isOwn: String(r.user_id ?? '') === String(user?.id ?? ''),
      }));
      // FIX #2 (2026-09-02, Jaime): preload message counts with the list.
      const enrichedApp = await withThreadMessages(mapped, appComplaintsRef.current);
      setAppComplaints(enrichedApp);
      // Unit-independent counters for the Soporte Vecindus tab: server-computed
      // for the caller's own threads; super_admin derives from the full list.
      if (isSuperAdmin()) {
        setAppStats(deriveAppStats(enrichedApp));
      } else {
        try {
          const stRes = await complaintsAPI.getMineAppStats();
          const stEnv = stRes && stRes.data !== undefined ? stRes.data : stRes;
          const st = stEnv && stEnv.data !== undefined ? stEnv.data : stEnv;
          if (st && typeof st.total === 'number') {
            setAppStats({ total: st.total, abiertas: st.abiertas ?? 0, enProceso: st.enProceso ?? 0, cerradas: st.cerradas ?? 0 });
          } else {
            throw new Error('bad stats payload');
          }
        } catch {
          setAppStats(deriveAppStats(enrichedApp));
        }
      }
      setAppLoaded(true);
      return true; // P1-1: report genuine success so refreshAllSilent can clear the gate
    } catch (err) {
      console.error('Error loading app complaints:', err);
      if (!opts?.silent) setAppComplaints([]);
      setAppLoaded(true);
      return false; // P1-1: failure (incl. swallowed 429) must not clear the backoff gate
    }
  };

  const loadComplaints = async (opts?: { silent?: boolean }) => {
    // SILENT REFRESH (2026-09-02, Jaime): background polls must not flash the
    // loading spinner over an already-rendered list.
    if (!opts?.silent) setLoading(true);
    try {
      // FIX #112: use the LIVE context user (localStorage 'user' is a stale
      // login/activateContext snapshot; see handleCreateComplaint note).
      const ctxListUser = user || (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
      const condoId = ctxListUser.condoId;
      // FIX (2026-09-23): users without a condo (orphan accounts, fresh signups)
      // fired GET /complaints/condo/undefined + /stats on first load -> cosmetic
      // 400/500 errors + error console spam + empty state. Render a clean empty
      // state instead; Soporte Vecindus (app tab) still works condo-independently.
      // FIX #343 (2026-10-06): the missing-condoId guard (#112 era) let a stale
      // NON-UUID condoId through, firing GET /complaints/condo/<stale> and
      // /stats -> 400 "Parámetro condoId inválido". Validate the UUID shape
      // BEFORE any complaintsAPI call; on invalid, render the standard empty
      // state (the Soporte Vecindus app tab still works condo-independently).
      if (!isUuid(condoId)) {
        setComplaints([]);
        setStats({ total: 0, abiertas: 0, enProceso: 0, cerradas: 0 });
        setLoading(false);
        return;
      }
      const response = await complaintsAPI.getAll(condoId);
      // Axios returns the API envelope in response.data: { success, data: rows }.
      // Unwrap both layers; treating the envelope itself as the rows silently
      // turned every successful list request into an empty list.
      const envelope = response && response.data !== undefined ? response.data : response;
      const payload = envelope && envelope.data !== undefined ? envelope.data : envelope;
      const rows = Array.isArray(payload) ? payload : [];
      // Map snake_case API rows to the camelCase Complaint shape with safe defaults.
      const mapped = rows.map((r: any) => ({
        ...r,
        id: String(r.id),
        condoId: r.condoId ?? r.condo_id,
        userId: r.userId ?? r.user_id,
        createdBy: r.createdBy ?? r.created_by ?? r.user_id ?? '',
        createdByName: r.createdByName ?? r.created_by_name ?? 'Vecino',
        createdByUnit: r.createdByUnit ?? r.unit ?? '',
        creatorRole: r.creatorRole ?? r.creator_role ?? '',
        status: normalizeComplaintStatus(r.status),
        createdAt: r.createdAt ?? r.created_at ?? '',
        updatedAt: r.updatedAt ?? r.updated_at ?? '',
        assignedTo: r.assignedTo ?? r.assigned_to,
        scope: (r.scope ?? 'condo') as 'condo' | 'app',
        isPrivate: !!(r.isPrivate ?? r.is_private),
        images: Array.isArray(r.images) ? r.images : [],
        attachmentUrl: r.attachmentUrl ?? r.attachment_url,
        attachmentUrls: mapAttachmentUrls(r),
        messages: Array.isArray(r.messages) ? r.messages : [],
      }));
      // FIX #2 (2026-09-02, Jaime): message-count badges previously only appeared
      // after opening a thread, because `messages` was empty until then. The
      // backend list does not include messages_count, so fetch each visible
      // case's thread once, in parallel, and cache it in state.
      const enriched = await withThreadMessages(mapped, complaintsRef.current);
      setComplaints(enriched);
      // Server-computed counters (Abiertas/En Proceso/Cerradas semantics live in the API).
      // Fallback: derive from the normalized rows if the stats endpoint fails.
      try {
        const statsRes = await complaintsAPI.getStats(condoId);
        const statsEnvelope = statsRes && statsRes.data !== undefined ? statsRes.data : statsRes;
        const s2 = statsEnvelope && statsEnvelope.data !== undefined ? statsEnvelope.data : statsEnvelope;
        if (s2 && typeof s2.total === 'number') {
          setStats({ total: s2.total, abiertas: s2.abiertas ?? 0, enProceso: s2.enProceso ?? 0, cerradas: s2.cerradas ?? 0 });
        } else {
          throw new Error('bad stats payload');
        }
      } catch {
        // FIX #3 (2026-09-02, Jaime): root cause of the wrong "En Proceso" counts —
        // this fallback detected "replied" via c.messages, which was empty until the
        // thread was opened, so En Proceso undercounted. Threads are now preloaded
        // above (withThreadMessages), so the fallback matches the server semantics:
        // En Proceso = not closed AND at least one admin/assistant reply.
        const closed = (v: any) => v === 'resolved';
        const hasReply = (c: any) => (c.messages || []).some((m: any) => m.sender === 'admin' || m.sender === 'assistant');
        const condoRows = enriched.filter((c: any) => (c.scope ?? 'condo') === 'condo');
        setStats({
          total: condoRows.length,
          abiertas: condoRows.filter((c: any) => !closed(c.status) && !hasReply(c)).length,
          enProceso: condoRows.filter((c: any) => !closed(c.status) && hasReply(c)).length,
          cerradas: condoRows.filter((c: any) => closed(c.status)).length,
        });
      }
      return true; // P1-1: report genuine success so refreshAllSilent can clear the gate
    } catch (err) {
      console.error('Error loading complaints:', err);
      // SILENT REFRESH: keep the previous list on a failed background poll.
      if (!opts?.silent) setComplaints([]);
      return false; // P1-1: failure (incl. swallowed 429) must not clear the backoff gate
    } finally {
      setLoading(false);
    }
  };

  // PACKET (2026-09-24, Jaime): super-admin bugs trend chart loader.
  const loadBugTrends = async (opts?: { window?: string; from?: string; to?: string }) => {
    setTrendLoading(true);
    try {
      const params: { window?: string; from?: string; to?: string } =
        opts?.from && opts?.to ? { from: opts.from, to: opts.to } : { window: opts?.window ?? (trendWindow === 'custom' ? '30' : trendWindow) };
      const res = await complaintsAPI.getBugTrends(params);
      const env = res && res.data !== undefined ? res.data : res;
      const d = env && env.data !== undefined ? env.data : env;
      setBugTrends(d ?? null);
    } catch {
      setBugTrends(null);
    } finally {
      setTrendLoading(false);
    }
  };

  // SILENT REFRESH (2026-09-02, Jaime): cross-user changes (e.g. a super_admin
  // closing a case) used to be invisible until a manual page refresh, on both
  // sides. A lightweight, non-stacking poll keeps the condo list + stats, the
  // Soporte Vecindus list + stats, and (via the existing 4s thread poll) the
  // open thread current. Runs only while the tab is visible, never stacks
  // (in-flight guard), and failures keep the previous UI state.
  const refreshInFlightRef = useRef(false);
  const lastRefreshAtRef = useRef(0);
  // Phase-1 429-incident fix (2026-09-30): min gap between background refreshes
  // (the 12s interval plus visibilitychange ticks could stack bursts) and an
  // exponential-backoff hold while the api.ts 429 gate is armed (the gate is
  // armed globally by the response interceptor; cleared on any success).
  const MIN_REFRESH_GAP_MS = 10000;
  const refreshAllSilent = async () => {
    if (refreshInFlightRef.current) return;
    if (is429Backoff()) return; // rate-limited: hold background polling
    const now = Date.now();
    if (now - lastRefreshAtRef.current < MIN_REFRESH_GAP_MS) return;
    refreshInFlightRef.current = true;
    try {
      // P1-1 fix (2026-09-30, Phase-1b peer review): the loaders swallow errors
      // (incl. 429s) internally, so reset429Backoff() used to run unconditionally
      // and cleared the gate armed by a 429 on every 12s tick, letting the
      // fan-out loop continue. Reset only on genuine success; the api.ts success
      // interceptor also clears the gate on any successful request.
      const okCondo = await loadComplaints({ silent: true });
      const okApp = appLoaded ? await loadAppComplaints({ silent: true }) : true;
      if (okCondo && okApp) reset429Backoff();
      lastRefreshAtRef.current = Date.now();
    } catch { /* silent */ }
    finally { refreshInFlightRef.current = false; }
  };

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      refreshAllSilent();
    };
    const id = setInterval(tick, 12000);
    // Refresh immediately when the user comes back to the tab.
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [appLoaded]);

  // PACKET (2026-09-24): trend chart loads once when the app tab opens (super-admin only).
  useEffect(() => {
    if (activeTab === 'app' && isSuperAdmin()) loadBugTrends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Filter complaints by user permissions first (condo isolation)
  const permissionFilteredComplaints = useDataFilter(complaints);
  
  // A1: hard-exclude app-scope rows from the condo tab for every role.
  const condoOnly = permissionFilteredComplaints.filter(c => (c.scope ?? 'condo') === 'condo');

  // Then apply UI filters (status, category, search)
  const filteredComplaints = condoOnly.filter(c => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (categoryFilter !== 'all' && c.category !== categoryFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        c.title.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query) ||
        c.createdByName.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // App-scope filter (including super-admin triage view). Unlike condo rows,
  // app rows are loaded separately, so apply the same chips here explicitly.
  const filteredAppComplaints = appComplaints.filter(c => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (categoryFilter !== 'all' && c.category !== categoryFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return c.title.toLowerCase().includes(query) ||
        c.description.toLowerCase().includes(query) ||
        c.createdByName.toLowerCase().includes(query);
    }
    return true;
  });

  // FEATURE (2026-09-02, Jaime): closed/resolved complaints sort to the BOTTOM
  // of the list; within each group, newest first. Applied to both tabs.
  const sortByClosedLast = (list: Complaint[]) =>
    [...list].sort((a, b) => {
      const aClosed = a.status === 'resolved' ? 1 : 0;
      const bClosed = b.status === 'resolved' ? 1 : 0;
      if (aClosed !== bClosed) return aClosed - bClosed;
      return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
    });

  // FEATURE (2026-09-23, Jaime 19:49): COMMUNITY RANKED LIST (app tab only).
  // Suggestions rank by net votes (up - down) desc, tie-break newest first, and
  // lead the list so top-voted suggestions visibly move to the top; vote
  // actions patch state in place, so the order re-sorts live after each toggle.
  // Non-suggestion rows keep closed-last / newest-first order below them.
  const sortByVotes = (list: Complaint[]) => {
    const isSug = (c: Complaint) => c.category === 'sugerencia';
    const sugg = list.filter(isSug).sort((a, b) => {
      const d = (b.voteScore ?? 0) - (a.voteScore ?? 0);
      if (d !== 0) return d;
      return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
    });
    return [...sugg, ...sortByClosedLast(list.filter(c => !isSug(c)))];
  };

  // Super-admin split views: Todo keeps the full ranked list; Bugs = private
  // intake triage (owner identity + status); Sugerencias = ranked by votes.
  const rankedAppList = sortByVotes(filteredAppComplaints);
  // PACKET (2026-09-24, Jaime): closed items move OUT of the open lists into
  // separate collapsed sections — 'Bugs resueltas' (super-admin Bugs tab splits
  // Abiertas/Resueltas) and 'Sugerencias resueltas' (also visible to the
  // community — good product signal). Todo keeps the full ranked list.
  const bugsSplit = (() => {
    const l = rankedAppList.filter(c => c.category === 'bug');
    return { open: l.filter(c => c.status !== 'resolved'), resolved: l.filter(c => c.status === 'resolved') };
  })();
  const sugsSplit = (() => {
    const l = rankedAppList.filter(c => c.category === 'sugerencia');
    return { open: l.filter(c => c.status !== 'resolved'), resolved: l.filter(c => c.status === 'resolved') };
  })();
  const resolvedBugs = bugsSplit.resolved;
  const resolvedSugs = sugsSplit.resolved;
  // Community (non-super-admin) sees ONLY non-private suggestion counts (bugs
  // are private intake); resolved suggestions are extracted from the main list.
  const communitySugCount = rankedAppList.filter(c => c.category === 'sugerencia' && !c.isPrivate).length;
  const openAppList = isSuperAdmin()
    ? (appSubTab === 'bugs' ? bugsSplit.open : appSubTab === 'sugerencias' ? sugsSplit.open : rankedAppList)
    : rankedAppList.filter(c => !(c.category === 'sugerencia' && c.status === 'resolved'));
  const renderedAppList = openAppList;

  const getStatusBadge = (status: BackendComplaintStatus) => {
    switch (status) {
      case 'resolved':
        return <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-bold rounded-full"><CheckCircle className="w-3 h-3" /> {STATUS_LABELS[status]}</span>;
      case 'in_progress':
        return <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 text-sm font-bold rounded-full"><Clock className="w-3 h-3" /> {STATUS_LABELS[status]}</span>;
      default:
        return <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-bold rounded-full"><AlertCircle className="w-3 h-3" /> {STATUS_LABELS.open}</span>;
    }
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'queja':
        return <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">Queja</span>;
      case 'sugerencia':
        return <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">Sugerencia</span>;
      case 'bug':
        return <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">Bug</span>;
      default:
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-bold rounded-full">Petición</span>;
    }
  };

  // FEATURE (2026-09-02, Jaime): show what type of user created each card.
  // Returns '' when the role is unknown/absent so the badge is simply hidden.
  const getCreatorRoleBadge = (role?: string) => {
    const v = String(role ?? '').trim().toLowerCase();
    if (!v) return null;
    const labels: Record<string, string> = {
      admin: 'Admin',
      super_admin: 'Super Admin',
      superadmin: 'Super Admin',
      owner: 'Propietario',
      propietario: 'Propietario',
      member: 'Residente',
      residente: 'Residente',
      resident: 'Residente',
      empleado: 'Personal',
      personal: 'Personal',
      staff: 'Personal',
    };
    const label = labels[v] ?? v.charAt(0).toUpperCase() + v.slice(1);
    return (
      <span title={`Creado por: ${label}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full">
        <User className="w-3 h-3" /> {label}
      </span>
    );
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'alta': return 'text-red-500';
      case 'media': return 'text-amber-500';
      default: return 'text-emerald-500';
    }
  };

  // Phase B (2026-09-01, Jaime): chat persists via /complaints/:id/messages.
  const openThread = async (complaint: Complaint) => {
    setSelectedComplaint(complaint);
    try {
      const res = await api.get(`/complaints/${complaint.id}/messages`);
      const rows = res.data?.data || [];
      setComplaints(prev => prev.map(c => c.id === complaint.id ? { ...c, messages: rows.map(threadToMessage) } : c));
      setSelectedComplaint(prev => prev && prev.id === complaint.id ? { ...prev, messages: rows.map(threadToMessage) } : prev);
    } catch { /* thread stays with local state if load fails */ }
  };

  const threadToMessage = (row: any): Message => ({
    id: String(row.id),
    text: row.body,
    sender: row.author === 'user' ? 'resident' : (row.author === 'assistant' ? 'assistant' : 'admin'),
    senderName: row.author === 'assistant' ? 'Soporte Vecindus (asistente)' : (row.author === 'admin' ? 'Administración' : 'Usuario'),
    timestamp: row.created_at,
  });

  // FEATURE (2026-09-02, Jaime): live updates for the open thread (4s poll).
  useEffect(() => {
    if (!selectedComplaint) return;
    const id = selectedComplaint.id;
    const poll = setInterval(async () => {
      // SILENT REFRESH: don't poll while the tab is hidden.
      if (document.visibilityState !== 'visible') return;
      // Phase-1 429 fix (2026-09-30): skip ticks while the 429 backoff gate is
      // armed instead of hammering the exhausted bucket every 4s.
      if (is429Backoff()) return;
      try {
        const res = await api.get(`/complaints/${id}/messages`);
        const rows = res.data?.data || [];
        setSelectedComplaint(prev => prev && prev.id === id ? { ...prev, messages: rows.map(threadToMessage) } : prev);
        setComplaints(prevC => prevC.map(c => c.id === id ? { ...c, messages: rows.map(threadToMessage) } : c));
      } catch { /* silent */ }
    }, 4000);
    return () => clearInterval(poll);
  }, [selectedComplaint?.id]);

  const [votingId, setVotingId] = useState<string | null>(null);
  const handleVote = async (complaint: Complaint, value: 1 | -1) => {
    if (complaint.category !== 'sugerencia' || votingId === complaint.id) return;
    setVotingId(complaint.id);
    try {
      const response = await api.put(`/complaints/${complaint.id}/vote`, { value });
      const raw = response?.data?.data ?? {};
      const patch = {
        voteScore: Number(raw.score ?? raw.voteScore ?? 0),
        voteUp: Number(raw.up ?? raw.voteUp ?? 0),
        voteDown: Number(raw.down ?? raw.voteDown ?? 0),
        myVote: Number(raw.myVote ?? raw.my_vote ?? 0),
      };
      setAppComplaints(prev => prev.map(c => c.id === complaint.id ? { ...c, ...patch } : c));
      setSelectedComplaint(prev => prev && prev.id === complaint.id ? { ...prev, ...patch } : prev);
    } catch (err) {
      console.error('Error voting suggestion:', err);
    } finally {
      setVotingId(null);
    }
  };

  // ==== Suggestion comments (2026-09-23, Jaime ruling 19:13) ====
  const [comments, setComments] = useState<Record<string, SuggestionComment[]>>({});
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const selectedIsAppSuggestion = !!selectedComplaint && selectedComplaint.scope === 'app' && selectedComplaint.category === 'sugerencia';

  // Load the community thread whenever a suggestion's detail opens. Bugs never
  // fetch comments (private intake; the API 404s them for non-owners anyway).
  useEffect(() => {
    setNewComment('');
    if (!selectedComplaint || selectedComplaint.scope !== 'app' || selectedComplaint.category !== 'sugerencia') return;
    const id = selectedComplaint.id;
    let cancelled = false;
    setCommentsLoading(true);
    api.get(`/complaints/${id}/comments`)
      .then((res) => {
        if (cancelled) return;
        const rows = res.data?.data || [];
        setComments(prev => ({ ...prev, [id]: rows.map((r: any) => ({
          id: String(r.id), userId: String(r.user_id ?? ''), authorName: r.author_name ?? 'Usuario', body: r.body, createdAt: r.created_at,
        })) }));
      })
      .catch(() => { if (!cancelled) setComments(prev => ({ ...prev, [id]: [] })); })
      .finally(() => { if (!cancelled) setCommentsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedComplaint?.id, selectedIsAppSuggestion]);

  const handleCommentSubmit = async () => {
    if (!selectedComplaint || !newComment.trim() || commentSubmitting) return;
    const body = newComment.trim();
    setCommentSubmitting(true);
    try {
      const res = await api.post(`/complaints/${selectedComplaint.id}/comments`, { body });
      const r = res.data?.data ?? {};
      const c: SuggestionComment = {
        id: String(r.id),
        userId: String(r.user_id ?? user?.id ?? ''),
        authorName: r.author_name ?? user?.name ?? 'Usuario',
        body: r.body ?? body,
        createdAt: r.created_at ?? new Date().toISOString(),
      };
      const id = selectedComplaint.id;
      setComments(prev => ({ ...prev, [id]: [...(prev[id] || []), c] }));
      const patchCount = (x: Complaint): Complaint => x.id === id ? { ...x, commentCount: (x.commentCount ?? 0) + 1 } : x;
      setAppComplaints(prev => prev.map(patchCount));
      setSelectedComplaint(prev => prev && prev.id === id ? patchCount(prev) : prev);
      setNewComment('');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'No se pudo publicar el comentario.');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedComplaint) return;
    const text = newMessage.trim();
    setNewMessage('');
    try {
      const res = await api.post(`/complaints/${selectedComplaint.id}/messages`, { author: isAdmin() ? 'admin' : 'user', body: text });
      const row = res.data?.data;
      if (row) {
        const message = threadToMessage(row);
        setComplaints(prev => prev.map(c =>
          c.id === selectedComplaint.id
            ? { ...c, messages: [...c.messages, message], updatedAt: new Date().toISOString() }
            : c
        ));
        setSelectedComplaint(prev => prev && prev.id === selectedComplaint.id ? { ...prev, messages: [...prev.messages, message] } : prev);
      }
    } catch (err: any) {
      setNewMessage(text); // restore draft on failure
    }
  };

  // FEATURE (2026-09-02, Jaime): wire the chat paperclip to the existing
  // complaint attachment endpoint (POST /complaints/:id/attachment — creator or
  // super_admin only, one image per complaint, base64 JPEG <=1024px like the
  // avatar flow; no new backend surface).
  // 2026-09-02: match backend POST /complaints/:id/attachment — creator, super_admin,
  // or condo admin roles (admin/treasurer/sub_admin) may attach images in the thread.
  const isCondoAdminRole = ['admin', 'treasurer', 'sub_admin'].includes(user?.role ?? '');
  const canAttachInThread = !!selectedComplaint && (
    isSuperAdmin() || isCondoAdminRole || String(selectedComplaint.userId ?? selectedComplaint.createdBy ?? '') === String(user?.id ?? '')
  );

  const handleThreadAttach = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    e.target.value = '';
    if (!f || !selectedComplaint) return;
    if (!f.type.startsWith('image/')) {
      setThreadAttachError('El archivo debe ser una imagen (JPEG, PNG, GIF o WebP).');
      return;
    }
    setThreadAttachError('');
    setThreadAttaching(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const url = URL.createObjectURL(f);
        const img = new Image();
        img.onload = () => {
          try {
            const maxDim = 1024;
            const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(img.width * scale));
            canvas.height = Math.max(1, Math.round(img.height * scale));
            canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
          } finally { URL.revokeObjectURL(url); }
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('formato')); };
        img.src = url;
      });
      const res = await api.post(`/complaints/${selectedComplaint.id}/attachment`, { file: dataUrl });
      const attUrl = res.data?.data?.attachmentUrl;
      if (attUrl) {
        const append = (c: Complaint): Complaint => {
          const prevUrls = Array.isArray(c.attachmentUrls) && c.attachmentUrls.length
            ? c.attachmentUrls
            : (c.attachmentUrl ? [c.attachmentUrl] : []);
          return {
            ...c,
            attachmentUrl: attUrl,
            attachmentUrls: prevUrls.includes(attUrl) ? prevUrls : [...prevUrls, attUrl],
          };
        };
        setComplaints(prev => prev.map(c => c.id === selectedComplaint.id ? append(c) : c));
        setSelectedComplaint(prev => prev && prev.id === selectedComplaint.id ? append(prev) : prev);
      }
    } catch (err: any) {
      setThreadAttachError(err?.response?.data?.error || 'No se pudo adjuntar la imagen.');
    } finally {
      setThreadAttaching(false);
    }
  };

  const handleCloseComplaint = async (id: string) => {
    if (confirm('¿Marcar esta queja/sugerencia como cerrada?')) {
      try {
        await complaintsAPI.update(id, { status: 'resolved' });
        // SILENT UPDATE (2026-09-02, Jaime): reflect the close in place — list
        // rows, open thread, and the Vivas/En Proceso/Cerradas counters — for
        // whoever closed it AND for the other side once its next poll lands.
        const prior = complaints.find(c => c.id === id);
        const wasLive = !!prior && prior.status !== 'resolved';
        setComplaints(prev => prev.map(c =>
          c.id === id ? { ...c, status: 'resolved', updatedAt: new Date().toISOString() } : c
        ));
        setAppComplaints(prev => prev.map(c =>
          c.id === id ? { ...c, status: 'resolved', updatedAt: new Date().toISOString() } : c
        ));
        if (selectedComplaint?.id === id) {
          setSelectedComplaint(prev => prev && prev.id === id ? { ...prev, status: 'resolved', updatedAt: new Date().toISOString() } : prev);
        }
        if (wasLive && prior) {
          const bump = (s: ComplaintStats): ComplaintStats => ({
            ...s,
            abiertas: Math.max(0, s.abiertas - (prior.status === 'open' ? 1 : 0)),
            enProceso: Math.max(0, s.enProceso - (prior.status === 'in_progress' ? 1 : 0)),
            cerradas: s.cerradas + 1,
          });
          if (prior.scope === 'app') setAppStats(bump); else setStats(bump);
        }
        // Re-sync with the server stats (also pushes the update to other tabs).
        refreshAllSilent();
      } catch (err) {
        console.error('Error closing complaint:', err);
      }
    }
  };

  // FEATURE (2026-09-02, Jaime): super-admin delete of a complaint card.
  // Removes the row in place (both tab lists + open thread) and adjusts the
  // counters locally, then silent-refreshes from the server. Button visibility
  // is gated on isSuperAdmin() at both call sites (card + thread header).
  const [deletingComplaintId, setDeletingComplaintId] = useState<string | null>(null);
  const handleDeleteComplaint = async (id: string) => {
    if (!confirm('¿Eliminar definitivamente esta queja/sugerencia? Esta acción no se puede deshacer.')) return;
    setDeletingComplaintId(id);
    try {
      await complaintsAPI.remove(id);
      const prior = complaints.find(c => c.id === id) || appComplaints.find(c => c.id === id);
      const bump = (st: ComplaintStats): ComplaintStats => ({
        total: Math.max(0, st.total - 1),
        abiertas: Math.max(0, st.abiertas - (prior?.status === 'open' ? 1 : 0)),
        enProceso: Math.max(0, st.enProceso - (prior?.status === 'in_progress' ? 1 : 0)),
        cerradas: Math.max(0, st.cerradas - (prior?.status === 'resolved' ? 1 : 0)),
      });
      const drop = (c: Complaint) => c.id !== id;
      setComplaints(prev => prev.filter(drop));
      setAppComplaints(prev => prev.filter(drop));
      setSelectedComplaint(prev => (prev && prev.id === id ? null : prev));
      if (prior?.scope === 'app') setAppStats(bump); else setStats(bump);
      refreshAllSilent();
    } catch (err: any) {
      console.error('Error deleting complaint:', err);
      alert(err?.response?.data?.error || 'No se pudo eliminar la queja/sugerencia.');
    } finally {
      setDeletingComplaintId(null);
    }
  };

  // F57: Open admin response form for a specific complaint
  const openResponseForm = (complaint: Complaint) => {
    setResponseComplaintId(complaint.id);
    setResponseData({
      status: complaint.status === 'open' ? 'in_progress' : complaint.status,
      response: '',
    });
    setResponseError('');
    setShowResponseForm(true);
  };

  // F57: Submit admin response
  const handleResponseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!responseComplaintId) return;
    setResponseLoading(true);
    setResponseError('');
    try {
      const payload: any = { status: responseData.status };
      if (responseData.response.trim()) {
        payload.response = responseData.response.trim();
      }
      await complaintsAPI.update(responseComplaintId, payload);
      // Update local state
      setComplaints(prev => prev.map(c => {
        if (c.id !== responseComplaintId) return c;
        const updated = { ...c, status: responseData.status, updatedAt: new Date().toISOString() };
        if (responseData.response.trim()) {
          updated.messages = [...c.messages, {
            id: Date.now().toString(),
            text: responseData.response,
            sender: 'admin' as const,
            senderName: user?.name || 'Administración',
            timestamp: new Date().toISOString(),
          }];
        }
        return updated;
      }));
      // Update selected complaint if it's the one being responded to
      if (selectedComplaint && selectedComplaint.id === responseComplaintId) {
        const updated = { ...selectedComplaint, status: responseData.status };
        if (responseData.response.trim()) {
          updated.messages = [...selectedComplaint.messages, {
            id: Date.now().toString(),
            text: responseData.response,
            sender: 'admin' as const,
            senderName: user?.name || 'Administración',
            timestamp: new Date().toISOString(),
          }];
        }
        setSelectedComplaint(updated);
      }
      // SILENT UPDATE (2026-09-02, Jaime): mirror the status change into the
      // Soporte Vecindus tab list and re-sync counters without a reload.
      setAppComplaints(prev => prev.map(c =>
        c.id === responseComplaintId ? { ...c, status: responseData.status, updatedAt: new Date().toISOString() } : c
      ));
      refreshAllSilent();
      setShowResponseForm(false);
      setResponseComplaintId(null);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Error al enviar respuesta';
      setResponseError(typeof msg === 'string' ? msg : 'Error al enviar respuesta');
    } finally {
      setResponseLoading(false);
    }
  };

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-[var(--v-surface-1)] border-b border-[var(--v-border-light)] z-50">
        <div className="flex flex-wrap items-center justify-between px-4 sm:px-6 py-3 sm:py-0 sm:h-16 gap-y-2">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] rounded-xl shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-bold text-[var(--v-text-primary)] truncate">Quejas y Sugerencias</h1>
              <p className="text-[11px] sm:text-xs text-[var(--v-text-secondary)] truncate">Comunicación con administración</p>
            </div>
          </div>
          <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-3 w-full sm:w-auto">
            {(
              <div className="flex bg-[var(--v-surface-2)] rounded-xl p-1 border border-[var(--v-border-light)]">
                <button
                  onClick={() => setActiveTab('condo')}
                  className={`px-3 sm:px-4 py-2 text-sm font-bold rounded-lg ${activeTab === 'condo' ? 'bg-[#36CCD8] text-white' : 'text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'}`}
                >
                  Condominio
                </button>
                <button
                  onClick={() => setActiveTab('app')}
                  className={`px-3 sm:px-4 py-2 text-sm font-bold rounded-lg ${activeTab === 'app' ? 'bg-[#36CCD8] text-white' : 'text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'}`}
                >
                  Soporte Vecindus
                </button>
              </div>
            )}
            <button
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center gap-2 shrink-0"
            >
              <Plus className="w-4 h-4" /> Nueva
            </button>
          </div>
        </div>
      </header>

      <main className="p-4 sm:p-6 max-w-6xl mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[var(--v-surface-1)] rounded-2xl p-5 border border-[var(--v-border-light)] shadow-sm">
            <p className="text-sm text-[var(--v-text-secondary)] mb-1">Total</p>
            <p className="text-3xl font-bold text-[var(--v-text-primary)]">{(activeTab === 'condo' ? stats : appStats).total}</p>
          </div>
          <div className="bg-[var(--v-surface-1)] rounded-2xl p-5 border border-[var(--v-border-light)] shadow-sm">
            <p className="text-sm text-[var(--v-text-secondary)] mb-1">Vivas</p>
            <p className="text-3xl font-bold text-blue-500">
              {activeTab === 'condo' ? (
                <>
                  {liveCases}
                  {complaintsLimit !== null && (
                    <span className="text-lg font-semibold text-[var(--v-text-tertiary)]">/{complaintsLimit}</span>
                  )}
                </>
              ) : (
                appStats.abiertas + appStats.enProceso
              )}
            </p>
          </div>
          <div className="bg-[var(--v-surface-1)] rounded-2xl p-5 border border-[var(--v-border-light)] shadow-sm">
            <p className="text-sm text-[var(--v-text-secondary)] mb-1">En Proceso</p>
            <p className="text-3xl font-bold text-amber-500">{(activeTab === 'condo' ? stats : appStats).enProceso}</p>
          </div>
          <div className="bg-[var(--v-surface-1)] rounded-2xl p-5 border border-[var(--v-border-light)] shadow-sm">
            <p className="text-sm text-[var(--v-text-secondary)] mb-1">Cerradas</p>
            <p className="text-3xl font-bold text-emerald-500">{(activeTab === 'condo' ? stats : appStats).cerradas}</p>
          </div>
        </div>

        {/* PACKET (2026-09-24, Jaime): per-category count chips — computed from
            the existing list payloads (no new API). Community (non-super-admin)
            sees only non-private suggestion counts (bugs are private intake). */}
        <div className="flex flex-wrap items-center gap-2 mb-6 -mt-2">
          {activeTab === 'condo' ? (
            <>
              {([['queja','Quejas'],['sugerencia','Sugerencias'],['peticion','Peticiones']] as const).map(([cat, label]) => (
                <span key={cat} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-full text-sm">
                  <span className="font-bold text-[var(--v-text-secondary)]">{label}</span>
                  <span className="font-bold text-[#2DB8C4]">{condoOnly.filter(c => c.category === cat).length}</span>
                </span>
              ))}
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-full text-sm">
                <span className="font-bold text-[var(--v-text-secondary)]">Otros</span>
                <span className="font-bold text-[#2DB8C4]">{condoOnly.filter(c => !['queja','sugerencia','peticion'].includes(c.category)).length}</span>
              </span>
            </>
          ) : isSuperAdmin() ? (
            <>
              {([['bug','Bugs'],['sugerencia','Sugerencias'],['queja','Quejas']] as const).map(([cat, label]) => (
                <span key={cat} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-full text-sm">
                  <span className="font-bold text-[var(--v-text-secondary)]">{label}</span>
                  <span className="font-bold text-[#2DB8C4]">{filteredAppComplaints.filter(c => c.category === cat).length}</span>
                </span>
              ))}
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-full text-sm">
                <span className="font-bold text-[var(--v-text-secondary)]">Otros</span>
                <span className="font-bold text-[#2DB8C4]">{filteredAppComplaints.filter(c => !['bug','sugerencia','queja'].includes(c.category)).length}</span>
              </span>
            </>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-full text-sm" title="Los bugs son privados; solo se muestran sugerencias públicas">
              <span className="font-bold text-[var(--v-text-secondary)]">Sugerencias de la comunidad</span>
              <span className="font-bold text-[#2DB8C4]">{communitySugCount}</span>
            </span>
          )}
        </div>

        {/* Filters */}
        <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--v-text-tertiary)]" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 min-w-0">
              <select 
                value={filter} 
                onChange={(e) => setFilter(e.target.value as any)}
                className="w-full sm:w-auto px-4 py-2 bg-[var(--v-surface-2)] text-[var(--v-text-primary)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
              >
                <option value="all">Todos los estados</option>
                <option value="open">Abiertas</option>
                <option value="in_progress">En Proceso</option>
                <option value="resolved">Cerradas</option>
              </select>
              <select 
                value={categoryFilter} 
                onChange={(e) => setCategoryFilter(e.target.value as any)}
                className="w-full sm:w-auto px-4 py-2 bg-[var(--v-surface-2)] text-[var(--v-text-primary)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
              >
                <option value="all">Todas las categorías</option>
                <option value="queja">Quejas</option>
                <option value="sugerencia">Sugerencias</option>
                <option value="peticion">Peticiones</option>
                <option value="bug">Bugs</option>
              </select>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="space-y-4">
          {activeTab === 'app' && (
            <p className="text-sm text-[var(--v-text-secondary)]">
              {isSuperAdmin()
                ? 'Feedback de la app (Soporte Vecindus). Todos los casos de la app.'
                : 'Tus casos con Soporte Vecindus. Disponibles desde cualquier unidad o condominio.'}
            </p>
          )}
          {/* FEATURE (2026-09-23, Jaime 19:49): super-admin split views. */}
          {activeTab === 'app' && isSuperAdmin() && (
            <div className="flex bg-[var(--v-surface-2)] rounded-xl p-1 border border-[var(--v-border-light)] w-fit">
              {([['todo','Todo'],['bugs','Bugs'],['sugerencias','Sugerencias']] as const).map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setAppSubTab(k)}
                  className={`px-3 py-1.5 text-sm font-bold rounded-lg ${appSubTab === k ? 'bg-[#36CCD8] text-white' : 'text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'}`}
                >
                  {label}
                  {/* PACKET (2026-09-24, Jaime): count chips on the sub-tabs. */}
                  <span className={`ml-1.5 text-xs font-bold ${appSubTab === k ? 'text-white/80' : 'text-[var(--v-text-tertiary)]'}`}>
                    {k === 'todo'
                      ? filteredAppComplaints.length
                      : k === 'bugs'
                        ? filteredAppComplaints.filter(c => c.category === 'bug').length
                        : filteredAppComplaints.filter(c => c.category === 'sugerencia').length}
                  </span>
                </button>
              ))}
            </div>
          )}
          {/* PACKET (2026-09-24, Jaime): super-admin bugs trend chart. */}
          {activeTab === 'app' && isSuperAdmin() && (
            <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm p-4 sm:p-5 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h3 className="font-bold text-[var(--v-text-primary)]">Tendencia de bugs</h3>
                <div className="flex bg-[var(--v-surface-2)] rounded-xl p-1 border border-[var(--v-border-light)]">
                  {([['7','7d'],['30','30d']] as const).map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => { setTrendWindow(k as any); loadBugTrends({ window: k }); }}
                      className={`px-3 py-1 text-sm font-bold rounded-lg ${trendWindow === k ? 'bg-[#36CCD8] text-white' : 'text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'}`}
                    >
                      {label}
                    </button>
                  ))}
                  <button
                    onClick={() => setTrendWindow(w => (w === 'custom' ? '30' : 'custom'))}
                    className={`px-3 py-1 text-sm font-bold rounded-lg ${trendWindow === 'custom' ? 'bg-[#36CCD8] text-white' : 'text-[var(--v-text-secondary)] hover:text-[var(--v-text-primary)]'}`}
                  >
                    Rango
                  </button>
                </div>
              </div>
              {trendWindow === 'custom' && (
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <input type="date" value={trendFrom} onChange={e => setTrendFrom(e.target.value)}
                    className="px-3 py-1.5 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-lg text-sm text-[var(--v-text-primary)]" />
                  <span className="text-[var(--v-text-secondary)]">–</span>
                  <input type="date" value={trendTo} onChange={e => setTrendTo(e.target.value)}
                    className="px-3 py-1.5 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-lg text-sm text-[var(--v-text-primary)]" />
                  <button
                    onClick={() => trendFrom && trendTo && loadBugTrends({ from: trendFrom, to: trendTo })}
                    disabled={!trendFrom || !trendTo || trendLoading}
                    className="px-3 py-1.5 bg-[#36CCD8] text-white font-bold rounded-lg text-sm disabled:opacity-50"
                  >
                    Aplicar
                  </button>
                </div>
              )}
              {trendLoading ? (
                <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[var(--v-text-secondary)]" /></div>
              ) : bugTrends?.found ? (
                <>
                  {/* DATA CAVEAT (Jaime 2026-09-23): resolved_at has no backfill —
                      resolved/day is only meaningful from the migration date. */}
                  {bugTrends.resolvedTrackingFrom && String(bugTrends.window?.from ?? '') < String(bugTrends.resolvedTrackingFrom) && (
                    <p className="text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                      ⚠️ El seguimiento de resoluciones comienza el {bugTrends.resolvedTrackingFrom}; los valores de "resueltas" anteriores son 0 (sin backfill).
                    </p>
                  )}
                  <BugTrendChart data={bugTrends} />
                </>
              ) : (
                <p className="text-sm text-[var(--v-text-secondary)]">Sin datos de tendencia.</p>
              )}
            </div>
          )}
          {(() => {
          const renderAppCard = (complaint: Complaint) => {
            const rank = activeTab === 'app' && complaint.category === 'sugerencia'
              ? rankedAppList.filter(c => c.category === 'sugerencia').findIndex(c => c.id === complaint.id) + 1
              : 0;
            return (
            <div 
              key={complaint.id}
              onClick={() => openThread(complaint)}
              className={`bg-[var(--v-surface-1)] rounded-2xl border shadow-sm p-4 sm:p-6 hover:shadow-md transition-all cursor-pointer ${activeTab === 'app' && complaint.category === 'sugerencia' && complaint.isOwn ? 'border-[#36CCD8] ring-2 ring-[#36CCD8]/20' : 'border-[var(--v-border-light)]'}`}
            >
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {activeTab === 'app' && complaint.category === 'sugerencia' && (() => {
                      const r = rankedAppList.filter(c => c.category === 'sugerencia').findIndex(c => c.id === complaint.id) + 1;
                      return r > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-6 px-1.5 py-0.5 bg-[#36CCD8] text-white text-xs font-bold rounded-full" title="Ranking por votos netos">#{r}</span>
                      ) : null;
                    })()}
                    <h3 className="font-bold text-[var(--v-text-primary)]">{complaint.title}</h3>
                    {getCategoryBadge(complaint.category)}
                    {getStatusBadge(complaint.status)}
                    {complaint.isPrivate && (
                      <span title="Queja privada: solo el creador y la administración pueden verla" className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-200 text-slate-700 text-xs font-bold rounded-full">
                        <Lock className="w-3 h-3" /> Privada
                      </span>
                    )}
                  </div>
                  <p className="text-[var(--v-text-secondary)] line-clamp-2 mb-3">{complaint.description}</p>
                  {activeTab === 'app' && complaint.category === 'sugerencia' && (
                    <div className="flex items-center gap-1 mb-3" onClick={(e) => e.stopPropagation()} aria-label="Votar sugerencia">
                      <button type="button" disabled={votingId === complaint.id} onClick={() => handleVote(complaint, 1)}
                        title="Votar a favor" className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg font-bold text-sm ${complaint.myVote === 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] hover:text-emerald-600'}`}>
                        <ArrowUp className="w-4 h-4" /> {complaint.voteUp ?? 0}
                      </button>
                      <span className="min-w-8 text-center text-sm font-bold text-[var(--v-text-secondary)]" title="Puntuación neta">{complaint.voteScore ?? 0}</span>
                      <button type="button" disabled={votingId === complaint.id} onClick={() => handleVote(complaint, -1)}
                        title="Votar en contra" className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg font-bold text-sm ${complaint.myVote === -1 ? 'bg-red-100 text-red-700' : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)] hover:text-red-600'}`}>
                        <ArrowDown className="w-4 h-4" /> {complaint.voteDown ?? 0}
                      </button>
                      <span className="ml-2 inline-flex items-center gap-1 text-xs font-bold text-[var(--v-text-secondary)]" title="Comentarios de la comunidad">
                        <MessageCircle className="w-4 h-4" /> {complaint.commentCount ?? 0}
                      </span>
                      {complaint.isOwn && <span className="ml-2 text-xs font-bold text-[#36CCD8]">Tu sugerencia</span>}
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-sm text-[var(--v-text-secondary)]">
                    <span className="flex items-center gap-1"><User className="w-4 h-4" /> {complaint.createdByName || 'Vecino'}{complaint.createdByUnit ? ` (${complaint.createdByUnit})` : ''}</span>
                    {getCreatorRoleBadge(complaint.creatorRole)}
                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date(complaint.createdAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}</span>
                    <span className={`font-bold ${getPriorityColor(complaint.priority)}`}>Prioridad: {complaint.priority}</span>
                    {complaint.messages.length > 0 && (
                      <span className="flex items-center gap-1 text-[#36CCD8]"><MessageSquare className="w-4 h-4" /> {complaint.messages.length} mensajes</span>
                    )}
                  </div>
                </div>
                {(isAdmin() && complaint.status !== 'resolved' || isSuperAdmin()) && (
                  <div className="flex gap-2">
                    {isAdmin() && complaint.status !== 'resolved' && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); openThread(complaint); }}
                        className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center gap-2"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Chat
                      </button>
                    )}
                    {isAdmin() && complaint.status !== 'resolved' && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleCloseComplaint(complaint.id); }}
                        className="px-4 py-2 bg-emerald-100 text-emerald-700 font-bold rounded-xl hover:bg-emerald-200"
                      >
                        Cerrar
                      </button>
                    )}
                    {isSuperAdmin() && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteComplaint(complaint.id); }}
                        disabled={deletingComplaintId === complaint.id}
                        title="Eliminar (super admin)"
                        className="px-4 py-2 bg-red-100 text-red-700 font-bold rounded-xl hover:bg-red-200 flex items-center gap-2 disabled:opacity-50"
                      >
                        {deletingComplaintId === complaint.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        Eliminar
                      </button>
                    )}
                  </div>
                )}
              </div>
              {rank > 0 && (
                <div className="mt-3 pt-3 border-t border-[var(--v-border-light)] flex items-center gap-2">
                  <span className="inline-flex items-center justify-center min-w-7 px-2 py-0.5 bg-[#36CCD8]/10 text-[#2DB8C4] text-xs font-bold rounded-full" title="Posición por votos netos">#{rank}</span>
                  <span className="text-xs font-bold text-[var(--v-text-secondary)]">{rank === 1 ? 'Sugerencia más votada' : `Puesto ${rank} por votos`}</span>
                </div>
              )}
            </div>
            );
          };
          const openList: Complaint[] = activeTab === 'condo' ? sortByClosedLast(filteredComplaints) : openAppList;
          // PACKET (2026-09-24, Jaime): collapsed resolved sections. Open list stays default.
          const resolvedSection = (title: string, items: Complaint[], show: boolean, onToggle: () => void) =>
            items.length === 0 ? null : (
              <div className="bg-[var(--v-surface-1)] rounded-2xl border border-[var(--v-border-light)] shadow-sm">
                <button type="button" onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 text-left">
                  <span className="font-bold text-[var(--v-text-primary)] flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" /> {title} ({items.length})
                  </span>
                  <ChevronDown className={`w-5 h-5 text-[var(--v-text-secondary)] transition-transform ${show ? 'rotate-180' : ''}`} />
                </button>
                {show && <div className="px-4 pb-4 space-y-4">{items.map(renderAppCard)}</div>}
              </div>
            );
          return (
            <>
              {openList.map(renderAppCard)}
              {activeTab === 'app' && isSuperAdmin() && appSubTab === 'bugs' &&
                resolvedSection('Bugs resueltas', resolvedBugs, showResolvedBugs, () => setShowResolvedBugs(v => !v))}
              {activeTab === 'app' && (isSuperAdmin() ? appSubTab === 'sugerencias' : true) &&
                resolvedSection('Sugerencias resueltas', resolvedSugs, showResolvedSugs, () => setShowResolvedSugs(v => !v))}
            </>
          );
          })()}
          {(activeTab === 'condo' ? filteredComplaints : openAppList).length === 0 && (
            <div className="text-center py-12 text-[var(--v-text-secondary)]">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-[var(--v-text-tertiary)]" />
              <p>No hay quejas o sugerencias</p>
            </div>
          )}
        </div>
      </main>

      {/* Detail/Chat Modal */}
      {selectedComplaint && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-xl font-bold text-[var(--v-text-primary)]">{selectedComplaint.title}</h3>
                  {getCategoryBadge(selectedComplaint.category)}
                  {selectedComplaint.isPrivate && (
                    <span title="Queja privada: solo el creador y la administración pueden verla" className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-200 text-slate-700 text-xs font-bold rounded-full">
                      <Lock className="w-3 h-3" /> Privada
                    </span>
                  )}
                </div>
                <p className="text-sm text-[var(--v-text-secondary)] flex items-center gap-2 flex-wrap">Por {selectedComplaint?.createdByName} • {new Date(selectedComplaint?.createdAt || '').toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })} {getCreatorRoleBadge(selectedComplaint?.creatorRole)}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {isSuperAdmin() && (
                  <button 
                    onClick={() => handleDeleteComplaint(selectedComplaint.id)} 
                    disabled={deletingComplaintId === selectedComplaint.id}
                    title="Eliminar (super admin)"
                    className="p-2 hover:bg-red-100 rounded-xl disabled:opacity-50"
                  >
                    {deletingComplaintId === selectedComplaint.id ? <Loader2 className="w-6 h-6 text-red-600 animate-spin" /> : <Trash2 className="w-6 h-6 text-red-500" />}
                  </button>
                )}
                <button onClick={() => setSelectedComplaint(null)} className="p-2 hover:bg-[var(--v-surface-2)] rounded-xl">
                  <XCircle className="w-6 h-6 text-[var(--v-text-tertiary)]" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-[var(--v-text-primary)] mb-6">{selectedComplaint.description}</p>

              {(() => {
                const urls = selectedComplaint.attachmentUrls?.length
                  ? selectedComplaint.attachmentUrls
                  : (selectedComplaint.attachmentUrl ? [selectedComplaint.attachmentUrl] : []);
                if (urls.length === 0) return null;
                return (
                  <div className="mb-6">
                    <p className="text-sm font-bold text-[var(--v-text-secondary)] mb-2">
                      {urls.length === 1 ? 'Imagen adjunta' : `Imágenes adjuntas (${urls.length})`}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {urls.map((u, i) => (
                        <a key={`${u}-${i}`} href={u} target="_blank" rel="noreferrer" className="inline-block">
                          <img src={u} alt={`Imagen adjunta ${i + 1} de la queja`} className="max-h-40 sm:max-h-48 w-auto rounded-xl border border-[var(--v-border-light)] hover:opacity-90" />
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}
              
              {selectedIsAppSuggestion && (
                <div className="mb-6">
                  <h4 className="font-bold text-[var(--v-text-primary)] mb-3">
                    Comentarios de la comunidad{typeof selectedComplaint.commentCount === 'number' ? ` (${selectedComplaint.commentCount})` : ''}
                  </h4>
                  {commentsLoading ? (
                    <p className="text-sm text-[var(--v-text-tertiary)] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando comentarios...</p>
                  ) : (comments[selectedComplaint.id]?.length ?? 0) === 0 ? (
                    <p className="text-sm text-[var(--v-text-tertiary)]">Aún no hay comentarios. ¡Sé el primero en comentar!</p>
                  ) : (
                    <div className="space-y-3">
                      {(comments[selectedComplaint.id] || []).map(cm => (
                        <div key={cm.id} className="bg-[var(--v-surface-2)] rounded-xl p-3">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-bold text-[var(--v-text-primary)]">{cm.authorName}{String(cm.userId) === String(user?.id ?? '') ? ' (tú)' : ''}</p>
                            <p className="text-xs text-[var(--v-text-tertiary)]">{new Date(cm.createdAt).toLocaleDateString('es-MX')}</p>
                          </div>
                          <p className="text-sm text-[var(--v-text-primary)] whitespace-pre-wrap break-words">{cm.body}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {selectedComplaint.messages.length > 0 && (
                <div className="space-y-4 mb-6">
                  <h4 className="font-bold text-[var(--v-text-primary)]">Conversación</h4>
                  {selectedComplaint.messages.map(msg => {
                    const isMine = msg.sender === 'resident';
                    return (
                    <div key={msg.id} className={`flex gap-3 ${isMine ? 'flex-row-reverse' : 'flex-row'}`}>
                      <div className={`p-4 rounded-2xl max-w-[80%] ${msg.sender === 'assistant' ? 'bg-[#F29D38]/10 text-[var(--v-text-primary)]' : msg.sender === 'admin' ? 'bg-[#36CCD8]/10 text-[var(--v-text-primary)]' : 'bg-[var(--v-surface-2)] text-[var(--v-text-primary)]'}`}>
                        <p className="text-sm font-bold mb-1">{msg.senderName}</p>
                        <p>{msg.text.replace(/^\[CLASIFICACION:\s*(\w+)\]\s*/i, '')}</p>
                        <p className="text-xs text-[var(--v-text-tertiary)] mt-2">{new Date(msg.timestamp).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            {selectedComplaint.status !== 'resolved' && !(selectedIsAppSuggestion && !selectedComplaint.isOwn) && (
              <div className="p-4 border-t border-[var(--v-border-light)]">
                {threadAttachError && <p className="text-red-500 text-sm font-bold mb-2">{threadAttachError}</p>}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Escribe un mensaje..."
                    className="flex-1 px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  />
                  <input type="file" accept="image/*" className="hidden" id="thread-image-input" onChange={handleThreadAttach} />
                  {canAttachInThread ? (
                    <label
                      htmlFor="thread-image-input"
                      title="Adjuntar imagen"
                      className={`p-3 rounded-xl cursor-pointer flex items-center ${threadAttaching ? 'text-[#36CCD8]' : 'text-[var(--v-text-tertiary)] hover:text-[#36CCD8] hover:bg-[#36CCD8]/10'}`}
                    >
                      {threadAttaching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
                    </label>
                  ) : (
                    <span
                      title="Solo el creador de la queja o una administración puede adjuntar una imagen"
                      className="p-3 rounded-xl text-[var(--v-text-tertiary)] opacity-40 cursor-not-allowed flex items-center"
                    >
                      <Paperclip className="w-5 h-5" />
                    </span>
                  )}
                  <button 
                    onClick={handleSendMessage}
                    className="px-6 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Community comment composer: non-owner viewers of a PUBLIC suggestion
                comment here instead of the private support thread (403 there). */}
            {selectedIsAppSuggestion && !selectedComplaint.isOwn && selectedComplaint.status !== 'resolved' && (
              <div className="p-4 border-t border-[var(--v-border-light)]">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Escribe un comentario público..."
                    maxLength={2000}
                    className="flex-1 px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
                    onKeyPress={(e) => e.key === 'Enter' && handleCommentSubmit()}
                  />
                  <button
                    onClick={handleCommentSubmit}
                    disabled={commentSubmitting}
                    className="px-6 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center gap-2 disabled:opacity-50"
                  >
                    {commentSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Complaint Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-4 px-0 pt-0 flex-shrink-0">
              <h3 className="text-xl font-bold text-[var(--v-text-primary)]">Nueva Queja/Sugerencia</h3>
              <button onClick={() => setShowNewModal(false)} className="p-2 hover:bg-[var(--v-surface-2)] rounded-xl">
                <XCircle className="w-6 h-6 text-[var(--v-text-tertiary)]" />
              </button>
            </div>
            
            <form className="space-y-4 overflow-y-auto flex-1 pr-1 -mr-1" onSubmit={(e) => { e.preventDefault(); if (!newSubmitting) handleCreateComplaint(); }}>
              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Título</label>
                <input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none" placeholder="Resumen breve..." />
              </div>
              
              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Categoría</label>
                <select value={newCategory} onChange={(e) => setNewCategory(e.target.value as 'queja' | 'sugerencia' | 'peticion' | 'bug')} className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none">
                  <option value="queja">Queja</option>
                  <option value="sugerencia">Sugerencia</option>
                  <option value="peticion">Petición</option>
                  <option value="bug">Bug</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Destino</label>
                <select value={newScope} onChange={(e) => setNewScope(e.target.value as 'condo' | 'app')} className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none">
                  <option value="condo">Mi condominio</option>
                  <option value="app">Soporte Vecindus (app)</option>
                </select>
              </div>

              {/* FEATURE (2026-09-02, Jaime): private case toggle — condo scope only. */}
              {newScope === 'condo' && (
                <div>
                  <label className="flex items-center gap-3 cursor-pointer select-none" title="Solo tú y la administración la verán">
                    <input
                      type="checkbox"
                      checked={newPrivate}
                      onChange={(e) => setNewPrivate(e.target.checked)}
                      className="w-5 h-5 accent-[#36CCD8]"
                    />
                    <span className="text-sm font-bold text-[var(--v-text-primary)] flex items-center gap-2">
                      <Lock className="w-4 h-4 text-[var(--v-text-secondary)]" /> Privada
                    </span>
                    <span className="text-xs text-[var(--v-text-tertiary)]">Solo tú y la administración la verán</span>
                  </label>
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Descripción</label>
                <textarea value={newDescription} onChange={(e) => setNewDescription(e.target.value)} className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none h-32 resize-none" placeholder="Describe tu queja o sugerencia en detalle..." />
              </div>
              
              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Adjuntar imagen (opcional)</label>
                <input type="file" accept="image/*" className="hidden" id="queja-image-input"
                  onChange={(e) => { const f = e.target.files?.[0] || null; e.target.value=''; if (f && !f.type.startsWith('image/')) { setNewError('El archivo debe ser una imagen.'); return; } setNewError(''); setNewImage(f); setNewImagePreview(f ? URL.createObjectURL(f) : ''); }} />
                <label htmlFor="queja-image-input" className="w-full py-3 border-2 border-dashed border-[var(--v-border-light)] rounded-xl text-[var(--v-text-tertiary)] hover:border-[#36CCD8] hover:text-[#36CCD8] flex items-center justify-center gap-2 cursor-pointer">
                  <ImageIcon className="w-5 h-5" /> {newImage ? newImage.name : 'Seleccionar imagen'}
                </label>
                {newImagePreview && (
                  <div className="mt-2 relative">
                    <img src={newImagePreview} alt="preview" className="max-h-32 sm:max-h-40 w-auto rounded-xl border border-[var(--v-border-light)]" />
                    <button type="button" onClick={() => { setNewImage(null); setNewImagePreview(''); }} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 text-xs">✕</button>
                  </div>
                )}
              </div>
              
              {newError && <p className="text-red-500 text-sm font-bold">{newError}</p>}
              <button
                type="button"
                disabled={newSubmitting}
                onClick={handleCreateComplaint}
                className="w-full py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {newSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {newSubmitting ? 'Enviando...' : 'Enviar'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* F57: Admin Response Modal */}
      {showResponseForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-[var(--v-text-primary)]">Responder Queja</h3>
              <button 
                onClick={() => { setShowResponseForm(false); setResponseComplaintId(null); }} 
                className="p-2 hover:bg-[var(--v-surface-2)] rounded-xl"
              >
                <XCircle className="w-6 h-6 text-[var(--v-text-tertiary)]" />
              </button>
            </div>
            <form onSubmit={handleResponseSubmit} className="space-y-4">
              {responseError && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
                  {responseError}
                </div>
              )}
              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Estado</label>
                <select
                  value={responseData.status}
                  onChange={(e) => setResponseData({ ...responseData, status: e.target.value as any })}
                  className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none"
                >
                  <option value="open">Abierta</option>
                  <option value="in_progress">En Proceso</option>
                  <option value="resolved">Cerrada</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-[var(--v-text-primary)] mb-2">Respuesta</label>
                <textarea
                  value={responseData.response}
                  onChange={(e) => setResponseData({ ...responseData, response: e.target.value })}
                  className="w-full px-4 py-3 bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-xl focus:border-[#36CCD8] focus:outline-none h-32 resize-none"
                  placeholder="Escribe tu respuesta..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowResponseForm(false); setResponseComplaintId(null); }}
                  className="flex-1 px-4 py-3 border border-[var(--v-border-light)] text-[var(--v-text-secondary)] rounded-xl font-bold hover:bg-[var(--v-surface-2)]"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={responseLoading}
                  className="flex-1 px-4 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {responseLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
                  ) : (
                    <><Send className="w-4 h-4" /> Enviar Respuesta</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* FIX #118/#119 (2026-09-24): non-blocking success toast host. */}
      <Toast toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
